import { useLatestDealsCompleted } from '../../query-hooks/latest-deals-completed';
import type { AreaData } from '@y2/api-clients/address-master-v1';
import { DesktopTable, DesktopTableSkeleton } from './desktop';
import { MobileList, MobileListSkeleton } from './mobile';
import { Project } from '../../query-hooks/gray-projects';
import { NoResults } from './no-results/no-results';
import styles from './deals-history.module.scss';
import { transformItem } from './types/mapping';
import { encodeCoords } from './coords/encode';
import { useId, useMemo } from 'react';

type Props = {
  project: Project;
  address: AreaData | undefined;
  headingSlot: React.ReactNode;
};

const itemsPerPage = 8;

export function DealsHistory({ project, address, headingSlot }: Props) {
  // eslint-disable-next-line id-length
  const { city, neighborhood, street, x, y } = project || {};
  const coords = x && y ? { lon: x, lat: y } : undefined;

  const { data, isLoading } = useLatestDealsCompleted(
    {
      limit: itemsPerPage,
      sortBy: 'distance',
      topArea: address?.top_area_id,
      area: address?.area_id,
      city: city?.id,
      neighborhood: neighborhood?.id,
      street: street?.id,
      coords: coords && encodeCoords(coords),
    },
    { enable: Boolean(address?.top_area_id && address?.area_id) },
  );

  const items = useMemo(() => data?.results?.map?.(transformItem), [data]);

  const headingId = useId();

  return (
    <section
      aria-labelledby={headingId}
      aria-live="polite"
      aria-atomic
      aria-busy={isLoading}
      className={styles.section}
    >
      <header>{headingSlot}</header>
      <div className={styles.table}>
        {isLoading && (
          <>
            <MobileListSkeleton size={itemsPerPage} className="mobile-only" />
            <DesktopTableSkeleton
              size={itemsPerPage}
              className="desktop-only"
            />
          </>
        )}
        {items && items.length > 0 && (
          <>
            <MobileList items={items} className="mobile-only" />
            <DesktopTable
              items={items}
              className="desktop-only"
              enableSortByAddress={Boolean(coords)}
            />
          </>
        )}
        {!items?.length && !isLoading && <NoResults />}
      </div>
    </section>
  );
}
